/**
 * Scribe import helpers (ER-11752 — Article Checkpoint import from Scribe).
 *
 * Shared by `ScribeImportDialog` (client-side link validation) and
 * `CheckpointEditFormV2` (backend error-code → user-facing message mapping).
 *
 * Backend source of truth: ER-11751 (Scribe Import — Fetch, Parse, and TipTap
 * Content Mapping). The service re-validates the link and raises these error
 * codes on a 4xx, shaped like `{ code: "<CODE>", ... }` (read from
 * `err.response.data.code`).
 */

/**
 * Accepted Scribe share-link shapes: scribehow.com/shared/…, scribehow.com/embed/…,
 * or scribehow.com/o/… (Scribe's current "Copy Link" share format).
 */
export const SCRIBE_LINK_PATTERN =
  /^(https?:\/\/)?(www\.)?scribehow\.com\/(shared|embed|o)\/[^\s]+$/i;

/** Inline copy shown when the pasted link fails client-side validation (no network call made). */
export const SCRIBE_LINK_INVALID_INPUT_MESSAGE = "Enter a valid Scribe share link.";

/** Backend error codes (Rule 2 + Rule 3 of the spec page). */
export const SCRIBE_IMPORT_ERROR_CODES = {
  INVALID_URL: "SCRIBE_IMPORT_INVALID_URL",
  NOT_FOUND: "SCRIBE_IMPORT_NOT_FOUND",
  PRIVATE_LINK: "SCRIBE_IMPORT_PRIVATE_LINK",
  PARSE_FAILED: "SCRIBE_IMPORT_PARSE_FAILED",
};

/** Clear, distinct user-facing copy for each backend error code. */
export const SCRIBE_IMPORT_ERROR_MESSAGES = {
  [SCRIBE_IMPORT_ERROR_CODES.INVALID_URL]:
    "That doesn't look like a Scribe share link. Paste a link copied from Scribe and try again.",
  [SCRIBE_IMPORT_ERROR_CODES.NOT_FOUND]:
    "We couldn't find that Scribe guide. Check the link and try again.",
  [SCRIBE_IMPORT_ERROR_CODES.PRIVATE_LINK]:
    "This Scribe guide is private. Make it public, then try again.",
  [SCRIBE_IMPORT_ERROR_CODES.PARSE_FAILED]:
    "We couldn't read that Scribe guide. Try a different link.",
};

/** Fallback message when the backend returns an unrecognised code or none at all. */
export const DEFAULT_SCRIBE_IMPORT_ERROR_MESSAGE = "Something went wrong. Please try again.";

/**
 * Client-side latency check (Rule 2): does the pasted value look like a public
 * Scribe share/embed link? The backend re-validates; this only avoids a wasted
 * network round-trip for obviously-wrong input.
 *
 * @param {string} url raw value pasted into the Scribe link field
 * @returns {boolean}
 */
export function isValidScribeLink(url) {
  if (typeof url !== "string") {
    return false;
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }
  return SCRIBE_LINK_PATTERN.test(trimmed);
}

/**
 * Resolve a backend error code to its user-facing message.
 *
 * @param {string} code backend error code (e.g. from err.response.data.code)
 * @param {string} [fallback] message used when the code is unknown
 * @returns {string}
 */
export function getScribeImportErrorMessage(code, fallback = DEFAULT_SCRIBE_IMPORT_ERROR_MESSAGE) {
  return SCRIBE_IMPORT_ERROR_MESSAGES[code] || fallback;
}

/**
 * Pull the error code out of an axios-style error response. Backend error codes
 * arrive as `err.response.data.code`; this also tolerates the `error` and
 * `detail` shapes so an unknown response layout still resolves when the code is
 * present.
 *
 * @param {*} error axios error (or anything thrown)
 * @returns {string} the error code, or "" when none is present
 */
export function resolveScribeImportErrorCode(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") {
    return "";
  }
  if (typeof data.code === "string" && data.code) {
    return data.code;
  }
  if (typeof data.error === "string" && data.error) {
    return data.error;
  }
  if (
    typeof data.detail === "string" &&
    Object.values(SCRIBE_IMPORT_ERROR_CODES).includes(data.detail)
  ) {
    return data.detail;
  }
  if (
    data.detail &&
    typeof data.detail === "object" &&
    typeof data.detail.code === "string" &&
    data.detail.code
  ) {
    return data.detail.code;
  }
  return "";
}

/**
 * Broken-image placeholder rendered for steps whose screenshot failed to
 * re-host (Rule 5 of the spec page). The backend emits an image node with a
 * null `src` and `placeholder: true`; `prepareScribeImportContent` swaps that
 * node's `src` for this marker so the editor shows a deliberate broken-image
 * placeholder instead of a browser missing-image glyph.
 */
export const SCRIBE_BROKEN_IMAGE_PLACEHOLDER_SRC = (() => {
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="320" height="180" viewBox="0 0 320 180">' +
    '<rect width="320" height="180" rx="8" fill="#f2f4f7"/>' +
    '<path fill="#98a2b3" d="M88 122l24-24 20 20 16-16 24 24 48 0 12 12 0 22-192 0 0-38z"/>' +
    '<circle cx="210" cy="62" r="14" fill="#98a2b3"/>' +
    '<path fill="#98a2b3" d="M224 46l4 8 8 4-8 4-4 8-4-8-8-4 8-4z"/>' +
    "</svg>";
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
})();

/**
 * Normalise the imported TipTap doc so failed re-hosts render as a visible
 * placeholder (Rule 5/6): image nodes the backend flagged with
 * `attrs.placeholder === true` (or which carry no `src`) get the broken-image
 * marker above. Returns a new doc; the input is never mutated.
 *
 * @param {object} content the `content` field of a successful scribe-import response
 * @returns {object} the normalised TipTap doc
 */
export function prepareScribeImportContent(content) {
  if (!content || typeof content !== "object") {
    return content;
  }
  let doc;
  try {
    doc = JSON.parse(JSON.stringify(content));
  } catch {
    return content;
  }
  rewriteBrokenImagePlaceholders(doc);
  return doc;
}

function rewriteBrokenImagePlaceholders(node) {
  if (Array.isArray(node)) {
    node.forEach(rewriteBrokenImagePlaceholders);
    return;
  }
  if (!node || typeof node !== "object") {
    return;
  }
  if (node.type === "image" && node.attrs && (node.attrs.placeholder === true || !node.attrs.src)) {
    node.attrs.src = SCRIBE_BROKEN_IMAGE_PLACEHOLDER_SRC;
    node.attrs.placeholder = true;
  }
  if (Array.isArray(node.content)) {
    node.content.forEach(rewriteBrokenImagePlaceholders);
  }
}
